import type { DespesaExtratoAvaliacaoDetalhe } from "../../../services/FinanceiroService";

export type ConferenciaCompLegacyPreview = {
  dadosBancarios?: Record<string, unknown>;
  parcelasExtrato?: Record<string, unknown>[];
  comprovantesDespesas?: Record<string, unknown>[];
  comprovanteSolicitacoes?: Record<string, unknown>[];
  situacaoEnvioComprovante?: string;
  liberarTotalParcelas?: boolean;
  obsAnalista?: string;
  comprovanteDespesasId?: number | null;
  extratoSolicitacaoId?: number | null;
  statusNome?: string | null;
};

const mapAnaliseToHistorico = (
  analise: unknown,
  idComprovanteDespesa: number,
  despesaEmConformidade: boolean | null = null,
): Record<string, unknown>[] => {
  if (!Array.isArray(analise)) return [];

  const maxId = Math.max(
    0,
    ...analise.map((raw) => Number((raw as Record<string, unknown>)?.id ?? (raw as Record<string, unknown>)?.Id ?? 0)),
  );

  return analise.map((raw) => {
    const item = (raw ?? {}) as Record<string, unknown>;
    const adequadoEm = item.adequadoEm ?? item.AdequadoEm ?? null;
    const obsAnalista =
      item.observacaoAnalista ?? item.ObservacaoAnalista ?? "";
    const emConformidade = item.emConformidade ?? item.EmConformidade;
    const isLatest = Number(item.id ?? item.Id ?? 0) === maxId;
    // emConformidade da despesa é o estado atual real (ex.: sindicato corrigiu
    // o item mas o analista ainda não reaprovou como conforme) — precisa vir
    // ANTES do palpite "adequadoEm preenchido => conforme", senão uma
    // correção já respondida mas ainda NAO_CONFORME aparecia como CONFORME.
    let conforme: boolean | null = null;
    if (typeof emConformidade === "boolean") conforme = emConformidade;
    else if (isLatest && typeof despesaEmConformidade === "boolean")
      conforme = despesaEmConformidade;
    else if (adequadoEm) conforme = true;
    else if (obsAnalista) conforme = false;

    return {
      id: item.id ?? item.Id,
      idComprovanteDespesa,
      obsSindicato: item.observacaoSindicato ?? item.ObservacaoSindicato ?? "",
      obsAnalista: obsAnalista ?? "",
      dataSolicitacao: item.solicitadoEm ?? item.SolicitadoEm ?? null,
      dataCorrecao: adequadoEm,
      conforme,
      correcaoRealizada: !!adequadoEm,
    };
  });
};

const pickAnaliseMaisRecente = (
  analise: unknown,
): Record<string, unknown> | null => {
  if (!Array.isArray(analise) || analise.length === 0) return null;
  return [...analise]
    .map((raw) => (raw ?? {}) as Record<string, unknown>)
    .sort(
      (a, b) =>
        Number(b?.id ?? b?.Id ?? 0) - Number(a?.id ?? a?.Id ?? 0),
    )[0];
};

const resolveConformeAtual = (
  hist: Record<string, unknown>[],
  analiseRaw: unknown,
  despesaEmConformidade: boolean | null = null,
): boolean | null => {
  const latest = pickAnaliseMaisRecente(analiseRaw);
  if (latest) {
    const emConformidade = latest.emConformidade ?? latest.EmConformidade;
    if (typeof emConformidade === "boolean") return emConformidade;

    const solicitado = latest.solicitadoEm ?? latest.SolicitadoEm ?? null;
    const adequado = latest.adequadoEm ?? latest.AdequadoEm ?? null;
    const obsAnalista =
      latest.observacaoAnalista ?? latest.ObservacaoAnalista ?? "";

    if (solicitado && !adequado) return null;
    if (typeof despesaEmConformidade === "boolean") return despesaEmConformidade;
    if (adequado) return true;
    if (String(obsAnalista).trim()) return false;
  }

  if (!hist.length) return null;
  const last = hist[hist.length - 1];
  if (typeof last?.conforme === "boolean") return last.conforme;
  return null;
};

/** Converte resposta V2 → shape consumido por ConferenciaCompDespesas. */
export const mapDespesasExtratoAvaliacaoDetalheToConferenciaPreview = (
  detalhe: DespesaExtratoAvaliacaoDetalhe,
  solicitacaoLabel = "1ª Solicitação",
): ConferenciaCompLegacyPreview => {
  const despesas = (detalhe.despesas ?? []).map((raw) => {
    const item = (raw ?? {}) as Record<string, unknown>;
    const tipo = (item.tipoDespesa ?? item.TipoDespesa ?? {}) as Record<
      string,
      unknown
    >;
    const idComp = Number(item.id ?? item.Id) || 0;
    const despesaEmConformidade =
      typeof item.emConformidade === "boolean"
        ? item.emConformidade
        : typeof item.EmConformidade === "boolean"
          ? item.EmConformidade
          : null;
    const historico = mapAnaliseToHistorico(
      item.analise,
      idComp,
      despesaEmConformidade,
    );
    const lastHist = historico.length
      ? [...historico].sort(
          (a, b) => Number(b?.id ?? 0) - Number(a?.id ?? 0),
        )[0]
      : null;
    const conforme = resolveConformeAtual(
      historico,
      item.analise,
      despesaEmConformidade,
    );
    const status =
      conforme === true
        ? "CONFORME"
        : conforme === false
          ? "NAO_CONFORME"
          : null;

    return {
      id: idComp,
      idComprovanteDespesa: idComp,
      idTipoDespesa: tipo.id ?? tipo.Id,
      descricao:
        String(item.descricao ?? item.Descricao ?? "").trim() ||
        String(tipo.titulo ?? tipo.Titulo ?? ""),
      valor: item.valor ?? item.Valor ?? 0,
      nomeArquivo: item.nomeArquivo ?? item.NomeArquivo ?? "",
      nomeComprovante: item.nomeArquivo ?? item.NomeArquivo ?? "",
      arquivoId: item.arquivoId ?? item.ArquivoId ?? null,
      identificador: String(idComp || item.arquivoId || ""),
      idAnalise: Number(lastHist?.id) || 0,
      status,
      conforme,
      obsAnalista: String(lastHist?.obsAnalista ?? ""),
      obsAnalistaOriginal: String(lastHist?.obsAnalista ?? ""),
      dataSolicOriginal: lastHist?.dataSolicitacao ?? null,
      historicoAnaliseAdequacaoDespesa: historico,
      primeiraParcela: true,
      segundaParcela: false,
    };
  });

  return {
    dadosBancarios: {
      tipo: detalhe.dadosBancarios?.tipo ?? "",
      tipoContaDescricao: String(
        detalhe.dadosBancarios?.tipoContaDescricao ??
          detalhe.dadosBancarios?.tipoConta ??
          detalhe.dadosBancarios?.tipo ??
          "",
      ),
      banco: detalhe.dadosBancarios?.banco ?? "",
      agencia: detalhe.dadosBancarios?.agencia ?? "",
      conta: detalhe.dadosBancarios?.conta ?? "",
    },
    parcelasExtrato: [
      {
        idTipoParcela: 1,
        descricao: solicitacaoLabel,
        valor: detalhe.valorAprovadoSolicitacao ?? 0,
        situacao: detalhe.status?.nome ?? "",
        solicitar: true,
      },
    ],
    comprovantesDespesas: despesas,
    comprovanteSolicitacoes: [],
    situacaoEnvioComprovante: detalhe.status?.nome ?? "",
    liberarTotalParcelas: !!(
      detalhe.liberarValorTotal ?? detalhe.liberarValorTotalSolicitacao
    ),
    obsAnalista: String(
      detalhe.observacaoAnalista ??
        detalhe.observacaoLiberarValorTotalSolicitacao ??
        "",
    ),
    comprovanteDespesasId:
      detalhe.comprovanteDespesasId ??
      detalhe.comprovanteDespesaId ??
      null,
    extratoSolicitacaoId:
      detalhe.extratoSolicitacaoId ??
      detalhe.extratoSolicitacaoAvaliacaoId ??
      null,
    statusNome: detalhe.status?.nome ?? null,
  };
};
